/**
 * Authentication service
 * Handles Firebase Authentication operations
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type UserCredential,
} from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

/**
 * Sign in with email and password
 */
export const signIn = async (email: string, password: string): Promise<UserCredential> => {
  return await signInWithEmailAndPassword(auth, email, password);
};

/**
 * Create a new user account
 */
export const createUser = async (email: string, password: string): Promise<UserCredential> => {
  return await createUserWithEmailAndPassword(auth, email, password);
};

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

/**
 * Change user password
 * Re-authenticates user and updates password in Firebase Auth
 * Also updates the user profile to clear requirePasswordChange flag
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  const user = auth.currentUser;

  if (!user || !user.email) {
    throw new Error('No user is currently signed in');
  }

  // Re-authenticate user with current password
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);

  // Update password in Firebase Auth
  await updatePassword(user, newPassword);

  // Update user profile to clear requirePasswordChange flag
  const userDocRef = doc(db, 'Users', user.uid);
  await updateDoc(userDocRef, {
    requirePasswordChange: false,
    lastPasswordChange: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};
